import { useEffect, useState } from "react";
import {
  Newspaper, MessageSquare, CheckCircle2, HelpCircle, Users, GraduationCap, ArrowDown, Workflow, MessagesSquare,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/admin/AdminShared";

const PIE = ["#0F294A", "#DC2626", "#2563EB", "#D97706", "#16A34A"];
const WORKFLOW = ["Input", "Verifikasi Substansi", "Review Hukum", "Pengelolaan Humas", "Approval", "Publikasi"];

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="text-slate-400">Memuat dashboard…</div>;
  const k = d.kpi;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-navy">Selamat datang, {user?.name} 👋</h1>
        <p className="text-slate-500 text-sm mt-1">Ringkasan aktivitas komunikasi publik HARMONI Bawaslu Sultra.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
        <StatCard icon={Newspaper} label="Total Berita" value={k.total_berita} testid="kpi-berita" />
        <StatCard icon={MessageSquare} label="Aspirasi Masuk" value={k.aspirasi_masuk} color="text-brandred bg-red-50" testid="kpi-aspirasi" />
        <StatCard icon={CheckCircle2} label="Aspirasi Selesai" value={k.aspirasi_selesai} color="text-green-600 bg-green-50" testid="kpi-selesai" />
        <StatCard icon={HelpCircle} label="Pertanyaan" value={k.pertanyaan} color="text-amber-600 bg-amber-50" testid="kpi-pertanyaan" />
        <StatCard icon={Users} label="Pengunjung" value={k.pengunjung.toLocaleString("id-ID")} color="text-blue-600 bg-blue-50" testid="kpi-pengunjung" />
        <StatCard icon={GraduationCap} label="Materi Edukasi" value={k.materi_edukasi} color="text-indigo-600 bg-indigo-50" testid="kpi-edukasi" />
        <StatCard icon={MessagesSquare} label="Chatbot Hari Ini" value={k.chatbot_hari_ini ?? 0} color="text-cyan-600 bg-cyan-50" testid="kpi-chatbot" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-bold text-navy mb-4">Statistik Publikasi per Bulan</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.publikasi_per_bulan}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="berita" fill="#0F294A" radius={[6, 6, 0, 0]} name="Berita" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-bold text-navy mb-4">Aspirasi Berdasarkan Status</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={d.aspirasi_by_status} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} label>
                {d.aspirasi_by_status.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-bold text-navy mb-4">Pengunjung Website</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.pengunjung_per_bulan}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line type="monotone" dataKey="visitors" stroke="#DC2626" strokeWidth={3} dot={{ r: 3 }} name="Pengunjung" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-bold text-navy mb-4">Pertanyaan Terbanyak</h3>
          <div className="space-y-3">
            {d.top_faq.map((f, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-600 truncate pr-2">{f.name}</span><span className="font-bold text-navy">{f.count}</span></div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-brandred" style={{ width: `${(f.count / d.top_faq[0].count) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-bold text-navy mb-4">Konten Paling Banyak Dilihat</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.top_content} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="views" fill="#2563EB" radius={[0, 6, 6, 0]} name="Dilihat" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-navy text-white rounded-xl p-5">
          <h3 className="font-bold flex items-center gap-2 mb-4"><Workflow className="h-5 w-5 text-brandred" /> Alur Verifikasi Konten</h3>
          <div className="flex flex-col gap-0">
            {WORKFLOW.map((w, i) => (
              <div key={w} className="flex flex-col items-start">
                <div className="w-full bg-white/10 rounded-lg px-3 py-2 text-sm font-semibold">{i + 1}. {w}</div>
                {i < WORKFLOW.length - 1 && <ArrowDown className="h-4 w-4 text-slate-400 my-1 ml-3" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
